import defaultStyles from "fond/draw/defaultStyles";
import { Configuration, MapboxStyleLayer } from "fond/types";
import { LayerStyle } from "fond/types/ProjectLayerConfig";

type BaseSymbolStyle = Pick<mapboxgl.SymbolLayer, "layout" | "paint">;

const baseSymbolStyle = {
  layout: {
    "text-field": ["case", ["==", ["get", "user_excluded"], "true"], "\uE85D", "\uE85C"],
    "text-anchor": "bottom",
    "text-justify": "center",
    // Note we prevent overlap and ignore placement to ensure only one icon is shown per feature location,
    // however take note that icons not rendered due to overlap are not returned in mapbox queryRenderedFeatures calls.
    "text-allow-overlap": false,
    "text-ignore-placement": false,
    "text-font": ["fontello Regular"],
    "text-padding": 0,
    "text-rotate": 0.01,
    "text-size": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 16],
    "text-line-height": -0.45,
  },
  paint: {
    "text-color": ["case", ["==", ["get", "user_excluded"], "true"], "red", "green"],
    "text-opacity": ["case", ["boolean", ["feature-state", "isEditing"], false], 0, 1],
    "text-translate": [-0.6, 0.1],
    "text-halo-width": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 2],
    "text-halo-color": "#ffffff",
  },
} as BaseSymbolStyle;

const baseHeatMapSymbolStyle = {
  ...baseSymbolStyle,
  layout: {
    ...baseSymbolStyle.layout,
    "text-field": ["case", ["==", ["get", "user_excluded"], "true"], "\uE80D", "\uE80A"],
  },
  paint: {
    ...baseSymbolStyle.paint,
    "text-color": "white",
    "text-halo-width": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 1],
    "text-opacity": ["interpolate", ["linear"], ["zoom"], 14, 0, 18, 1],
  },
} as BaseSymbolStyle;

/**
 * The MapboxDraw theme extended to include styles specific to planner projects
 * when drawing address features.
 */
const getDefaults = (baseStyle: BaseSymbolStyle, withHeatmap: boolean): MapboxStyleLayer[] =>
  defaultStyles.map((style) => {
    if (style.id === "gl-draw-point-stroke-active") {
      return {
        ...style,
        paint: {
          ...style.paint,
          ...(withHeatmap
            ? {
                "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 12],
                "circle-color": "rgba(255, 255, 255, 0)",
                "circle-stroke-color": "#FFF000",
                "circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 14, 1, 18, 3],
              }
            : {
                "circle-radius": 12,
                "circle-color": "#FFF000",
              }),
        },
      } as mapboxgl.CircleLayer;
    }

    if (style.id === "gl-draw-point-point-stroke-inactive") {
      return {
        ...style,
        paint: {
          ...style.paint,
          "circle-radius": 0,
        },
      } as mapboxgl.CircleLayer;
    }

    if (["gl-draw-point-active", "gl-draw-point-inactive"].includes(style.id)) {
      return {
        ...style,
        type: "symbol",
        layout: {
          ...baseStyle.layout,
        },
        paint: {
          ...baseStyle.paint,
        },
      } as mapboxgl.SymbolLayer;
    }

    return style;
  });

export const createStyles = ({
  styleId,
  source,
  sourceLayer,
  layerFilter,
  withHeatmap = false,
}: {
  styleId: string;
  source: string;
  sourceLayer: string;
  layerFilter: any[] | undefined;
  withHeatmap?: boolean;
}): MapboxStyleLayer[] => {
  const baseStyle = withHeatmap ? baseHeatMapSymbolStyle : baseSymbolStyle;
  const defaults = getDefaults(baseStyle, withHeatmap);
  return [
    {
      id: styleId,
      type: "symbol",
      source,
      "source-layer": sourceLayer,
      ...(layerFilter ? { filter: layerFilter } : {}),
      ...baseStyle,
    } as mapboxgl.SymbolLayer,
    ...defaults,
  ];
};

/**
 * Creates styles for the exclude draw mode based on the styles used for coloring by CTS.
 * We rely on the CTS Styles (heatmap) to have the same properties.FondID as the underlying service location.
 */
export const getCTSStyles = (config: Configuration, colorBy: string, excludedFondIDs: string[], source: string): mapboxgl.CircleLayer[] => {
  // Find all styles that belong to layers/sublayers with the matching Key
  const styles = Object.values(config.Data.entities).filter((entity): entity is LayerStyle => {
    if (entity?.Type !== "STYLE") return false;

    const parent = config.Data.entities[entity.ConfigurationID];
    return !!(parent && "Key" in parent && parent.Key === colorBy);
  });

  return styles.map((style) => {
    const mapboxStyle = style.MapboxStyle as mapboxgl.CircleLayer;
    const parent = config.Data.entities[style.ConfigurationID];

    // Get the filter from the parent sublayer - this is critical for CTS heatmaps
    // as each style represents a different cost range
    const filter = parent && "FilterConfiguration" in parent ? parent.FilterConfiguration?.Mapbox : undefined;

    return {
      ...mapboxStyle,
      id: `${style.ID}-exclude`,
      source,
      "source-layer": colorBy,
      ...(filter ? { filter } : {}),
      metadata: { layerKey: colorBy, isExcludeLayer: true },
      paint: {
        ...mapboxStyle.paint,
        "circle-color": ["case", ["in", ["get", "FondID"], ["literal", excludedFondIDs]], "rgba(0,0,0, 0.5)", mapboxStyle?.paint?.["circle-color"]],
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 12],
        "circle-stroke-opacity": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 1],
        "circle-stroke-color": "#FFF",
        "circle-stroke-width": ["case", ["in", ["get", "FondID"], ["literal", excludedFondIDs]], 0, 1],
      },
      layout: { ...mapboxStyle.layout, visibility: "visible" },
    } as mapboxgl.CircleLayer;
  });
};

// Remove any existing CTS styles
export const removeCTSStyles = (map: mapboxgl.Map): void => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const excludeLayers = map?.getStyle().layers?.filter((layer) => (layer as any).metadata?.isExcludeLayer === true);

  excludeLayers?.forEach((layer) => {
    if (map.getLayer(layer.id)) {
      map.removeLayer(layer.id);
    }
  });
};
